import { useState, useRef, useEffect, createContext, useContext } from "react";
import { SlotsToClasses } from "@nextui-org/react";
import { cn } from "@/libs/utils/tailwind/cn";
import { useMediaQuery } from "@/hooks/useMediaQuery";

export function FloatingMenu({
  children,
  className,
  isOpen: controlledIsOpen,
  onOpenChange,
}: FloatingMenuProps) {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const menuRef = useRef<HTMLDivElement>(null);
  const isOpen = controlledIsOpen ?? internalIsOpen;
  const isControlled = controlledIsOpen !== undefined;

  const preventCloseMenu = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  };

  const openMenu = () => {
    preventCloseMenu();
    if (!isControlled) {
      setInternalIsOpen(true);
      return;
    }

    onOpenChange?.(true);
  };

  const closeMenu = () => {
    if (isControlled) {
      onOpenChange?.(false);
      return;
    }

    // The Debounce here is to prevent the menu from closing too quickly when the user is hovering over the menu.
    timeoutRef.current = setTimeout(() => setInternalIsOpen(false), 150);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        closeMenu();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <FloatingMenuContext.Provider
      value={{ isOpen, openMenu, closeMenu, preventCloseMenu }}
    >
      <div ref={menuRef} className={cn("relative", className)}>{children}</div>
    </FloatingMenuContext.Provider>
  );
}

function FloatingMenuTrigger({
  children,
  className,
}: FloatingMenuTriggerProps) {
  const { openMenu, closeMenu } = useFloatingMenuContext();

  return (
    <div className={className} onMouseLeave={closeMenu} onMouseEnter={openMenu}>
      {children}
    </div>
  );
}

function FloatingMenuContent({
  children,
  className,
  classNames,
  width = "w-max",
  showArrow = true,
}: FloatingMenuContentProps) {
  const { isOpen, closeMenu, preventCloseMenu } = useFloatingMenuContext();
  const [hasEntered, setHasEntered] = useState(false);
  const isLandscapeCompact = useMediaQuery("(orientation: landscape) and (max-height: 768px) and (max-width: 1024px)");

  useEffect(() => {
    if (!isOpen) {
      setHasEntered(false);
      return;
    }

    const animationFrame = requestAnimationFrame(() => setHasEntered(true));

    return () => cancelAnimationFrame(animationFrame);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className={cn(
        "absolute z-50",
        isLandscapeCompact ? "bottom-full mb-3" : "top-full mt-3",
        "left-1/2 -translate-x-1/2",
        className
      )}
      onMouseLeave={closeMenu}
      onMouseEnter={preventCloseMenu}
    >
      {showArrow && (
        <div
          className={cn(
            "pointer-events-none absolute z-10 h-2 w-4 translate-z-0 bg-white",
            isLandscapeCompact
              ? "-bottom-2 [clip-path:polygon(50%_100%,0%_0%,100%_0%)]"
              : "-top-2 [clip-path:polygon(50%_0%,0%_100%,100%_100%)]",
            "left-1/2 -translate-x-1/2",
            classNames?.arrow
          )}
        />
      )}
      <div
        className={cn(
          "rounded-lg bg-white p-6 shadow-xl transition duration-150 ease-out will-change-transform motion-reduce:transition-none",
          isLandscapeCompact ? "origin-bottom" : "origin-top",
          hasEntered
            ? "translate-y-0 scale-100 opacity-100"
            : isLandscapeCompact
              ? "translate-y-1 scale-95 opacity-0"
              : "-translate-y-1 scale-95 opacity-0",
          width,
          classNames?.card
        )}
      >
        {children}
      </div>
    </div>
  );
}

interface FloatingMenuContextValue {
  isOpen: boolean;
  openMenu: () => void;
  closeMenu: () => void;
  preventCloseMenu: () => void;
}

const FloatingMenuContext = createContext<FloatingMenuContextValue | null>(
  null
);

function useFloatingMenuContext() {
  const context = useContext(FloatingMenuContext);
  if (!context) {
    throw new Error("FloatingMenu components must be used within FloatingMenu");
  }
  return context;
}


interface FloatingMenuProps {
  children: React.ReactNode;
  className?: string;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}


interface FloatingMenuTriggerProps {
  children: React.ReactElement;
  className?: string;
}

interface FloatingMenuContentProps {
  children: React.ReactNode;
  className?: string;
  classNames?: SlotsToClasses<"card" | "arrow">;
  width?: string;
  showArrow?: boolean;
}

FloatingMenu.Trigger = FloatingMenuTrigger;
FloatingMenu.Content = FloatingMenuContent;
